'use client'

import { useState, useMemo, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faChevronLeft, 
  faChevronRight, 
  faCalendarDay,
  faTv,
  faLocationDot,
  faVideo,
  faPhotoFilm,
  faSpinner,
  faExclamationTriangle,
  faRefresh,
  faSquare,
  faFlag,
  faSquareMinus
} from '@fortawesome/free-solid-svg-icons'
import Image from 'next/image'
import Link from 'next/link'
import { useFootballResults } from '@/hooks/useFootballResults'
import { getStatusText } from '@/lib/football-api'
import { generateMatchUrl } from '@/lib/slug-utils'
import '@/app/kqbd/kqbd.css'

// Generate dates for calendar navigation
const generateDates = (selectedDate: string) => {
  const selected = new Date(selectedDate + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const dates = []
  
  // Generate 7 days around selected date (-3 to +3)
  for (let i = -3; i <= 3; i++) {
    const date = new Date(selected)
    date.setDate(date.getDate() + i)
    
    const dateString = date.toISOString().split('T')[0]
    const isToday = date.getTime() === today.getTime()
    const isSelected = dateString === selectedDate
    
    // Get Vietnamese weekday names
    const weekdays = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7']
    const weekday = weekdays[date.getDay()]
    
    dates.push({
      date: dateString,
      weekday: weekday,
      day: date.getDate().toString(),
      month: `Th${date.getMonth() + 1}`,
      isToday,
      active: isSelected
    })
  }
  
  return dates
}

export default function KQBD() {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
  const [activeLeague, setActiveLeague] = useState('all')
  const [lastSelectedLeague, setLastSelectedLeague] = useState<string | null>(null)
  const [displayLimit, setDisplayLimit] = useState(15)
  
  const calendarRef = useRef<HTMLDivElement>(null)
  const leaguesRef = useRef<HTMLDivElement>(null)
  
  // Handle wheel scroll for horizontal scrolling
  const handleWheelScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    if (target && e.deltaY !== 0 && target.scrollWidth > target.clientWidth) {
      e.preventDefault()
      target.scrollLeft += e.deltaY * 0.5 // Slower scroll for better control
    }
  }

  // Quick jump to today
  const handleTodayJump = () => {
    const today = new Date().toISOString().split('T')[0]
    setSelectedDate(today)
    setActiveLeague('all')
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target && (e.target as HTMLElement).tagName === 'INPUT') return // Skip if typing in input
      
      switch (e.key.toLowerCase()) {
        case 'arrowleft':
          e.preventDefault()
          handlePrevDate()
          break
        case 'arrowright':
          e.preventDefault()
          handleNextDate()
          break
        case 't':
          e.preventDefault()
          handleTodayJump()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [selectedDate])
  
  // Use the football results hook - always fetch all leagues
  const { 
    matches, 
    groupedMatches, 
    loading, 
    error, 
    hasMore, 
    total, 
    refresh, 
    loadMore 
  } = useFootballResults({
    date: selectedDate,
    // league: 'all', // Remove this parameter since hook now always fetches 'all'
    limit: 20, // Limit to 20 matches
    autoRefresh: true,
    refreshInterval: 60000 // Refresh every minute
  })

  // Generate calendar dates
  const dates = useMemo(() => generateDates(selectedDate), [selectedDate])

  // Generate league filters from grouped matches with smart sorting
  const leagues = useMemo(() => {
    const leagueList = Object.values(groupedMatches).map(league => ({
      id: league.id,
      name: league.name,
      logo: league.logo,
      count: league.matches.length,
      priority: league.priority,
      matches: league.matches
    }))
    
    const allLeagues = [
      { 
        id: 'all', 
        name: 'Tất cả', 
        logo: '', 
        count: matches.length,
        priority: 0,
        matches: []
      },
      ...leagueList
    ]

    // Sort leagues: active league first (if not 'all'), then by priority
    return allLeagues.sort((a: any, b: any) => {
      // Nếu có active league và không phải 'all'
      if (activeLeague !== 'all') {
        if (a.id.toString() === activeLeague && b.id.toString() !== activeLeague) {
          return -1 // a lên đầu
        }
        if (b.id.toString() === activeLeague && a.id.toString() !== activeLeague) {
          return 1 // b lên đầu
        }
      }
      
      // Giữ 'Tất cả' luôn ở đầu
      if (a.id === 'all') return -1
      if (b.id === 'all') return 1
      
      // Sort theo priority
      return a.priority - b.priority
    })
  }, [groupedMatches, matches.length, activeLeague])

  // Get filtered matches based on active league (client-side filtering)
  const filteredMatches = useMemo(() => {
    if (activeLeague === 'all') {
      // Get matches up to the current display limit with smart sorting
      const allGroupsArray = Object.values(groupedMatches)
      
      // Sort groups with priority: last selected league first, then by priority
      const sortedGroups = allGroupsArray.sort((a, b) => {
        if (lastSelectedLeague) {
          if (a.id.toString() === lastSelectedLeague && b.id.toString() !== lastSelectedLeague) {
            return -1 // a lên đầu
          }
          if (b.id.toString() === lastSelectedLeague && a.id.toString() !== lastSelectedLeague) {
            return 1 // b lên đầu
          }
        }
        // Sort theo priority
        return a.priority - b.priority
      })
      
      let count = 0
      const result = []
      
      for (const league of sortedGroups) {
        for (const match of league.matches) {
          if (count >= displayLimit) break
          result.push(match)
          count++
        }
        if (count >= displayLimit) break
      }
      
      return result
    }
    
    const selectedLeague = groupedMatches[activeLeague]
    return selectedLeague ? selectedLeague.matches : []
  }, [matches, groupedMatches, activeLeague, displayLimit, lastSelectedLeague])

  // Reset display limit when changing date or league
  useEffect(() => {
    setDisplayLimit(15)
  }, [selectedDate, activeLeague])

  const handleLoadMore = () => {
    setDisplayLimit(prev => prev + 15)
  }

  const handleDateChange = (date: string) => {
    setSelectedDate(date)
    setActiveLeague('all') // Reset to all leagues when changing date
  }

  const handleLeagueChange = (leagueId: string | number) => {
    const leagueIdStr = leagueId.toString()
    setActiveLeague(leagueIdStr)
    
    // Lưu lại giải đấu được chọn (trừ 'all')
    if (leagueIdStr !== 'all') {
      setLastSelectedLeague(leagueIdStr)
    }
  }

  const handlePrevDate = () => {
    const currentDate = new Date(selectedDate + 'T00:00:00')
    currentDate.setDate(currentDate.getDate() - 1)
    setSelectedDate(currentDate.toISOString().split('T')[0])
  }

  const handleNextDate = () => {
    const currentDate = new Date(selectedDate + 'T00:00:00')
    currentDate.setDate(currentDate.getDate() + 1)
    setSelectedDate(currentDate.toISOString().split('T')[0])
  }

  // Auto scroll to active date
  useEffect(() => {
    if (calendarRef.current) {
      const activeButton = calendarRef.current.querySelector('.calendar-nav__date--active')
      if (activeButton) {
        activeButton.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest', 
          inline: 'center' 
        })
      }
    }
  }, [selectedDate])

  const formatMatchTime = (time: string | undefined | null) => {
    if (!time || typeof time !== 'string') {
      return '--:--' // Default fallback
    }
    return time.slice(0, 5) // Remove seconds
  }

  return (
    <div className="match-results">
      {/* Calendar Navigation */}
      <div className="calendar-nav">
        <button className="calendar-nav__arrow" onClick={handlePrevDate}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <div className="calendar-nav__dates" ref={calendarRef} onWheel={handleWheelScroll}>
          {dates.map((date, index) => (
            <button 
              key={index}
              className={`calendar-nav__date ${
                date.active ? 'calendar-nav__date--active' : ''
              } ${date.isToday ? 'calendar-nav__date--today' : ''}`}
              onClick={() => handleDateChange(date.date)}
            >
              <span className="calendar-nav__weekday">{date.weekday}</span>
              <span className="calendar-nav__day">{date.day}</span>
              <span className="calendar-nav__month">{date.month}</span>
              {date.isToday && <span className="calendar-nav__today-mark">HÔM NAY</span>}
            </button>
          ))}
        </div>
        <button className="calendar-nav__arrow" onClick={handleNextDate}>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
        <button className="calendar-nav__today-btn" onClick={handleTodayJump} title="Về hôm nay (phím T)">
          <FontAwesomeIcon icon={faCalendarDay} />
        </button>
      </div>

      {/* League Filters */}
      <div className="league-filters" ref={leaguesRef} onWheel={handleWheelScroll}>
        {leagues.map((league) => (
          <button
            key={league.id}
            className={`league-filters__button ${
              activeLeague === league.id.toString() ? 'league-filters__button--active' : ''
            }`}
            onClick={() => handleLeagueChange(league.id)}
          >
            {league.logo && (
              <Image
                src={league.logo}
                alt={league.name}
                width={20}
                height={20}
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            )}
            <span className="league-name">{league.name}</span>
            <span>{league.count}</span>
          </button>
        ))}
      </div>

      {/* Results Container */}
      <div className="results-grid">
        {/* Loading State - Show spinner overlay when loading and we have existing data */}
        {loading && matches.length > 0 && (
          <div className="loading-overlay">
            <FontAwesomeIcon icon={faSpinner} spin />
            <span>Đang tải dữ liệu...</span>
          </div>
        )}

        {/* Loading State - Show full loading when no data */}
        {loading && matches.length === 0 && (
          <div className="loading-state">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p>Đang tải kết quả...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="error-state">
            <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
            <div>
              {error.includes('No finished matches found') ? (
                <>
                  <p>Không có trận đấu nào đã kết thúc vào ngày {new Date(selectedDate).toLocaleDateString('vi-VN')}</p>
                  <p className="error-suggestion">
                    Hãy thử chọn ngày khác hoặc kiểm tra lại vào lúc khác
                  </p>
                </>
              ) : (
                <p>Có lỗi xảy ra: {error}</p>
              )}
              <button onClick={refresh} className="retry-btn">
                <FontAwesomeIcon icon={faRefresh} />
                Thử lại
              </button>
            </div>
          </div>
        )}

        {/* Results Section */}
        {!loading && !error && filteredMatches.length === 0 && (
          <div className="no-results">
            <FontAwesomeIcon icon={faSquare} size="2x" />
            <p>
              {activeLeague === 'all' 
                ? `Không có kết quả trận đấu nào cho ngày ${new Date(selectedDate).toLocaleDateString('vi-VN')}`
                : `Không có kết quả nào cho giải đấu này vào ngày ${new Date(selectedDate).toLocaleDateString('vi-VN')}`
              }
            </p>
            <p className="no-results-suggestion">
              Thử chọn ngày khác hoặc giải đấu khác
            </p>
          </div>
        )}

        {/* Display results grouped by league - TABLE FORMAT */}
        {leagues
          .filter(league => league.id !== 'all')
          .map((league) => (
            <section key={league.id} className="results-section">
              <div className="league-schedule">
                <div className="league-header">
                  <Image 
                    src={league.logo} 
                    alt={league.name}
                    width={32}
                    height={32}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                  <h2>{league.name}</h2>
                  <span className="match-count">({league.count})</span>
                </div>

                {/* Table Format for Results */}
                <div className="kqbd-table-container">
                  {/* Table Header */}
                  <div className="kqbd-table-header">
                    <div className="kqbd-col kqbd-col-time">Giờ</div>
                    <div className="kqbd-col kqbd-col-home">Chủ nhà</div>
                    <div className="kqbd-col kqbd-col-score">Tỷ số</div>
                    <div className="kqbd-col kqbd-col-away">Khách</div>
                    <div className="kqbd-col kqbd-col-ht">HT</div>
                    <div className="kqbd-col kqbd-col-stats">
                      <FontAwesomeIcon icon={faFlag} style={{ color: '#888', fontSize: '12px' }} />
                      {' '}HT | FT
                    </div>
                    <div className="kqbd-col kqbd-col-cards">
                      <FontAwesomeIcon icon={faSquareMinus} style={{ color: '#f1c40f', fontSize: '12px' }} />
                      {' '}HT | FT
                    </div>
                  </div>

                  {/* Table Body */}
                  <div className="kqbd-table-body">
                    {league.matches.map((match) => (
                      <div key={match.match_id} className="kqbd-table-row">
                        <div className="kqbd-col kqbd-col-time">
                          {formatMatchTime(match.match_time)}
                        </div>
                        
                        <div className="kqbd-col kqbd-col-home">
                          <span className="team-name">{match.home_team_name}</span>
                          <Image 
                            src={match.home_team_crest}
                            alt={match.home_team_name}
                            width={20}
                            height={20}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                        </div>

                        <div className="kqbd-col kqbd-col-score">
                          <span className="kqbd-score-display">
                            {match.home_score !== null && match.away_score !== null 
                              ? `${match.home_score} : ${match.away_score}`
                              : '- : -'
                            }
                          </span>
                        </div>

                        <div className="kqbd-col kqbd-col-away">
                          <Image 
                            src={match.away_team_crest}
                            alt={match.away_team_name}
                            width={20}
                            height={20}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                          <span className="team-name">{match.away_team_name}</span>
                        </div>

                        <div className="kqbd-col kqbd-col-ht">
                          <span className="ht-score">0 : 1</span>
                        </div>

                        <div className="kqbd-col kqbd-col-stats">
                          <span className="stat-home">
                            {match.home_corners || 0} - {match.home_yellow_cards || 0}
                          </span>
                          <span className="stat-away">
                            {match.away_corners || 0} - {match.away_yellow_cards || 0}
                          </span>
                        </div>

                        <div className="kqbd-col kqbd-col-cards">
                          <span className="card-home">
                            {match.home_red_cards || 0} - {match.home_yellow_cards || 0}
                          </span>
                          <span className="card-away">
                            {match.away_red_cards || 0} - {match.away_yellow_cards || 0}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          ))}

        {/* Load More Button */}
        {activeLeague === 'all' && matches.length > displayLimit && (
          <div className="load-more-section">
            <button 
              onClick={handleLoadMore} 
              className="load-more-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Đang tải...
                </>
              ) : (
                'Tải thêm'
              )}
            </button>
          </div>
        )}

        {/* <div className="results-footer">
          <p>
            {activeLeague === 'all' 
              ? `Hiển thị ${Math.min(displayLimit, matches.length)} trận đấu / ${total} tổng cộng`
              : `Hiển thị ${filteredMatches.length} trận đấu`
            }
          </p>
          <p>Dữ liệu cập nhật tự động mỗi phút</p>
        </div> */}
      </div>
    </div>
  )
} 